import { Fragment } from "preact";
import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type {
  BudgetKey,
  ModeSwitchBody,
  RuntimeBudget,
  RuntimeLogRow,
  RuntimeModeRow,
  RuntimeModelChoice,
  RuntimeProvider,
  RuntimeRun,
  RuntimeSpend,
  RuntimeSpendDay,
  RuntimeView,
  WipePreview,
  WipeResponse,
} from "@/api/types";
import { PanelHelp } from "@/components/PanelHelp";
import { ReasonCard } from "@/components/ReasonCard";
import { RuntimeNotice } from "@/components/RuntimeNotice";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { type Save, useRuntimeChange } from "@/hooks/useRuntimeChange";
import { t, type TranslationKey } from "@/i18n";
import { formatDateTime, formatInt, formatPercent, formatUsd, modeLabel } from "@/lib/format";
import { runSummary } from "@/lib/reasoningDepth";
import { reasonValid, runtimeErrorMessage } from "@/lib/runtimeChange";

// «Betrieb»: the switches an admin changes without a deploy (chat and modes
// on or off, a mode's model and depth, the three budgets), the day's spend,
// the change log and the production-day reset. Every change carries the
// version the panel shows and a reason; the backend logs who saved it.

/** From this share of the daily budget on, the gauge turns orange. */
export const WARN_RATIO = 0.8;

// The depth select's value for «keep Terraform's depth»; "" is a real choice
// (send none: the model's own default).
const TERRAFORM = "terraform";

// Provider names are brands, the same in both languages.
const PROVIDER_LABEL: Record<RuntimeProvider, string> = { claude: "Claude", gemini: "Gemini" };

const BUDGET_LABEL: Record<BudgetKey, TranslationKey> = {
  anonymous_session_usd: "ops.budget.anonymous",
  signed_in_daily_usd: "ops.budget.signedIn",
  daily_usd: "ops.budget.daily",
};

/** The id of a budget's field, which the chat card can lead to. */
const budgetField = (key: BudgetKey): string => `ops-budget-${key}`;
/** The id of the quiet hours' start, which the chat card can lead to. */
const QUIET_START_FIELD = "ops-quiet-start";

// --- helpers ------------------------------------------------------------------

/** What the chat card offers. While the quiet hours or the day's budget hold
 * the chat, it leads to their fields first; pausing then holds the chat by
 * hand until someone switches it back on (maintenance). */
export type ChatAction = "pause" | "resume" | "budget" | "quietHours" | "pausePermanently";

export function chatActions(data: RuntimeView): ChatAction[] {
  if (!data.chat_enabled) return ["resume"];
  if (data.paused_reason === "schedule") return ["quietHours", "pausePermanently"];
  if (data.paused_reason === "budget") return ["budget", "pausePermanently"];
  return ["pause"];
}

const CHAT_ACTION_LABEL: Record<ChatAction, TranslationKey> = {
  pause: "ops.chat.pause",
  resume: "ops.chat.resume",
  budget: "ops.chat.raiseBudget",
  quietHours: "ops.chat.changeQuietHours",
  pausePermanently: "ops.chat.pausePermanently",
};

// The fields a chat card action leads to; focus brings them into view as well.
const ACTION_FIELD: Partial<Record<ChatAction, string>> = {
  budget: budgetField("daily_usd"),
  quietHours: QUIET_START_FIELD,
};

/** Why the chat is paused, in words. */
function pausedText(data: RuntimeView): string {
  if (data.paused_reason === "schedule" && data.quiet_hours) {
    return t("ops.chat.pausedBySchedule", {
      start: data.quiet_hours.start,
      end: data.quiet_hours.end,
    });
  }
  return t(data.paused_reason === "budget" ? "ops.chat.pausedByBudget" : "ops.chat.pausedBySwitch");
}

export type GaugeLevel = "off" | "ok" | "warn" | "reached";

/** Today's spend against the daily budget: the bar's fill (0 to 1) and level. */
export function budgetGauge(spend: RuntimeSpend): { fill: number; level: GaugeLevel } {
  const ratio = spend.used_ratio;
  if (ratio === null) return { fill: 0, level: "off" };
  const level: GaugeLevel = ratio >= 1 ? "reached" : ratio >= WARN_RATIO ? "warn" : "ok";
  return { fill: Math.min(ratio, 1), level };
}

const AMOUNT_RE = /^\d+(?:\.\d+)?$/;

/** A budget field: blank keeps Terraform's value (null); Swiss notation is
 * read (1'000,50). NaN when it is no amount of 0 or more. */
export function parseUsdInput(value: string): number | null {
  const text = value.trim().replace(/['’\s]/g, "").replace(",", ".");
  if (text === "") return null;
  return AMOUNT_RE.test(text) ? Number(text) : Number.NaN;
}

/** A Zurich day ("2026-09-23") the Swiss way (23.09.2026). */
export function formatDay(day: string): string {
  const [year, month, date] = day.split("-");
  return `${date}.${month}.${year}`;
}

/** The mode form's state. */
export interface ModeDraft {
  enabled: boolean;
  /** "" keeps Terraform's model. */
  model: string;
  /** null keeps Terraform's depth; "" sends none (the model's own default). */
  depth: string | null;
  scope: "all" | "test";
}

/** The form starts from the stored switch, or from Terraform without one. */
export function switchDraft(row: RuntimeModeRow): ModeDraft {
  const stored = row.override;
  return {
    enabled: stored ? stored.enabled : row.enabled,
    model: stored?.model ?? "",
    depth: stored ? stored.depth : null,
    scope: stored?.scope ?? "all",
  };
}

/** The body of POST /runtime/modes/{mode}. A chosen model brings its
 * provider, so a Claude model on a Gemini mode switches the provider too. */
export function modeSwitchBody(
  row: RuntimeModeRow,
  draft: ModeDraft,
  expectedVersion: number,
  reason: string,
): ModeSwitchBody {
  const choice = row.choices.find((c) => c.model_id === draft.model);
  return {
    expected_version: expectedVersion,
    reason: reason.trim(),
    enabled: draft.enabled,
    provider: choice?.provider ?? null,
    model: choice?.model_id ?? null,
    depth: draft.depth,
    scope: draft.scope,
  };
}

function choiceFor(row: RuntimeModeRow, model: string): RuntimeModelChoice | undefined {
  const id = model || row.baseline.model;
  return row.choices.find((c) => c.model_id === id);
}

/** The depths on offer: the chosen model's, else those of Terraform's model. */
export function depthOptions(row: RuntimeModeRow, model: string): string[] {
  return choiceFor(row, model)?.depths ?? [];
}

/** Whether the reset may be sent: configured, confirmed, a password, a reason. */
export function wipeReady(
  preview: WipePreview,
  confirm: string,
  password: string,
  reason: string,
): boolean {
  return (
    preview.available &&
    confirm === preview.confirm_text &&
    password.length > 0 &&
    reasonValid(reason)
  );
}

const sameRun = (a: RuntimeRun, b: RuntimeRun): boolean =>
  a.provider === b.provider && a.model === b.model && a.depth === b.depth;

const usdOrOff = (usd: number): string => (usd > 0 ? formatUsd(usd) : t("ops.budget.off"));

function byProvider(choices: readonly RuntimeModelChoice[]) {
  const groups = new Map<RuntimeProvider, RuntimeModelChoice[]>();
  for (const c of choices) groups.set(c.provider, [...(groups.get(c.provider) ?? []), c]);
  return [...groups.entries()];
}

// --- the view -----------------------------------------------------------------

export function OpsView() {
  const change = useRuntimeChange();
  const { poll, data, reason, canSave, save } = change;

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={poll.lastFetched}
          loading={poll.loading}
          error={poll.error}
          onRefresh={poll.refetch}
        />
      </div>

      <ReasonCard change={change} />
      <RuntimeNotice change={change} />

      {data && (
        <>
          <div class="grid grid-3">
            <ChatCard data={data} reason={reason} canSave={canSave} onSave={save} />
            <SpendCard spend={data.spend} />
            {/* Keyed on the version: a save or a poll with new values restarts the draft. */}
            <QuietHoursCard
              key={data.version}
              data={data}
              reason={reason}
              canSave={canSave}
              onSave={save}
            />
          </div>
          {/* Keyed on the version: a save or a poll with new values restarts the drafts. */}
          <BudgetsCard
            key={data.version}
            data={data}
            reason={reason}
            canSave={canSave}
            onSave={save}
          />
          <ModesCard data={data} reason={reason} canSave={canSave} onSave={save} />
          <LogCard log={data.log} />
        </>
      )}

      <WipeCard
        version={data?.version}
        reason={reason}
        onLogged={() => change.setReason("")}
        onDone={poll.refetch}
      />
    </div>
  );
}

interface CardProps {
  data: RuntimeView;
  reason: string;
  canSave: boolean;
  onSave: Save;
}

function ChatCard({ data, reason, canSave, onSave }: CardProps) {
  const paused = data.paused_reason !== null;
  const budgetSpent = data.spend.used_ratio !== null && data.spend.used_ratio >= 1;
  const act = (action: ChatAction) => {
    const field = ACTION_FIELD[action];
    if (field) {
      document.getElementById(field)?.focus();
      return;
    }
    void onSave(() =>
      api.switchChat({
        expected_version: data.version,
        reason: reason.trim(),
        enabled: action === "resume",
      }),
    );
  };
  return (
    <section class="card">
      <h2>{t("ops.chat")}</h2>
      <PanelHelp k="ops.chat" />
      <div class={paused ? "kpi hl-orange" : "kpi"}>
        {paused ? t("ops.chat.paused") : t("ops.chat.on")}
      </div>
      {paused && <div class="kpi-sub">{pausedText(data)}</div>}
      {!data.chat_enabled && budgetSpent && (
        <div class="kpi-sub">{t("ops.chat.budgetAlsoSpent")}</div>
      )}
      {data.updated_at && (
        <div class="kpi-sub">
          {t("ops.lastChange", { when: formatDateTime(data.updated_at), who: data.updated_by })}
        </div>
      )}
      <div class="toolbar ops-form">
        {chatActions(data).map((action, i) => (
          <button
            key={action}
            type="button"
            class={i === 0 ? "window-pill active" : "window-pill"}
            // Leading to a field changes nothing, so it needs no reason.
            disabled={!ACTION_FIELD[action] && !canSave}
            onClick={() => act(action)}
          >
            {t(CHAT_ACTION_LABEL[action])}
          </button>
        ))}
      </div>
      {chatActions(data).includes("pausePermanently") && (
        <div class="kpi-sub">{t("ops.chat.pausePermanentlyHint")}</div>
      )}
    </section>
  );
}

function QuietHoursCard({ data, reason, canSave, onSave }: CardProps) {
  const [start, setStart] = useState(data.quiet_hours?.start ?? "");
  const [end, setEnd] = useState(data.quiet_hours?.end ?? "");
  const complete = start !== "" && end !== "" && start !== end;
  const send = (next: { start: string | null; end: string | null }) =>
    void onSave(() =>
      api.setQuietHours({ expected_version: data.version, reason: reason.trim(), ...next }),
    );

  return (
    <section class="card">
      <h2>{t("ops.quiet")}</h2>
      <PanelHelp k="ops.quiet" />
      <div class="kpi-sub">
        {data.quiet_hours ? t("ops.quiet.current", { ...data.quiet_hours }) : t("ops.quiet.none")}
      </div>
      <form
        class="toolbar ops-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (complete) send({ start, end });
        }}
      >
        <label class="toggle">
          <span>{t("ops.quiet.from")}</span>
          <input
            id={QUIET_START_FIELD}
            type="time"
            value={start}
            onInput={(e) => setStart((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="toggle">
          <span>{t("ops.quiet.to")}</span>
          <input
            type="time"
            value={end}
            onInput={(e) => setEnd((e.target as HTMLInputElement).value)}
          />
        </label>
        <button type="submit" class="window-pill active" disabled={!canSave || !complete}>
          {t("ops.quiet.save")}
        </button>
        <button
          type="button"
          class="window-pill"
          disabled={!canSave || !data.quiet_hours}
          onClick={() => send({ start: null, end: null })}
        >
          {t("ops.quiet.remove")}
        </button>
      </form>
    </section>
  );
}

/** Today's spend, with a meter against the daily budget when there is one. */
export function SpendGauge({ spend }: { spend: RuntimeSpend }) {
  const gauge = budgetGauge(spend);
  const ratio = spend.used_ratio ?? 0;
  return (
    <div>
      <div class="kpi">{formatUsd(spend.today_usd)}</div>
      <div class="kpi-sub">
        {gauge.level === "off"
          ? t("ops.spend.noBudget")
          : t("ops.spend.ofBudget", {
              budget: formatUsd(spend.daily_budget_usd),
              percent: formatPercent(ratio),
            })}
        {" · "}
        {t("ops.spend.turns", { n: formatInt(spend.today_turns) })}
      </div>
      {gauge.level !== "off" && (
        <div
          class="funnel-track ops-gauge"
          role="meter"
          aria-label={t("ops.spend")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(Math.round(ratio * 100), 100)}
          aria-valuetext={formatPercent(ratio)}
        >
          <div
            class={`funnel-fill ops-gauge-${gauge.level}`}
            style={{ width: `${gauge.fill * 100}%` }}
          />
        </div>
      )}
      {gauge.level === "warn" && <div class="hl-orange">{t("ops.spend.warn")}</div>}
      {gauge.level === "reached" && <div class="hl-orange">{t("ops.spend.reached")}</div>}
    </div>
  );
}

function SpendDays({ days, budget }: { days: RuntimeSpendDay[]; budget: number }) {
  const top = Math.max(budget, ...days.map((d) => d.usd), 0.01);
  return (
    <div class="ops-days">
      {days.map((d) => (
        <div
          key={d.day}
          class="ops-day"
          title={`${formatDay(d.day)} · ${formatUsd(d.usd)} · ${t("ops.spend.turns", { n: formatInt(d.turns) })}`}
        >
          <div
            class={budget > 0 && d.usd >= budget * WARN_RATIO ? "ops-day-fill ops-day-warn" : "ops-day-fill"}
            style={{ height: `${(d.usd / top) * 100}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function SpendCard({ spend }: { spend: RuntimeSpend }) {
  return (
    <section class="card">
      <h2>{t("ops.spend")}</h2>
      <PanelHelp k="ops.spend" />
      <SpendGauge spend={spend} />
      <SpendDays days={spend.days} budget={spend.daily_budget_usd} />
      <div class="kpi-sub">{t("ops.spend.days", { total: formatUsd(spend.last_30_days_usd) })}</div>
    </section>
  );
}

function budgetDrafts(budgets: readonly RuntimeBudget[]): Record<BudgetKey, string> {
  const drafts: Record<BudgetKey, string> = {
    anonymous_session_usd: "",
    signed_in_daily_usd: "",
    daily_usd: "",
  };
  for (const b of budgets) if (b.overridden) drafts[b.key] = String(b.effective_usd);
  return drafts;
}

function BudgetsCard({ data, reason, canSave, onSave }: CardProps) {
  const [drafts, setDrafts] = useState(() => budgetDrafts(data.budgets));
  const values = {
    anonymous_session_usd: parseUsdInput(drafts.anonymous_session_usd),
    signed_in_daily_usd: parseUsdInput(drafts.signed_in_daily_usd),
    daily_usd: parseUsdInput(drafts.daily_usd),
  };
  const invalid = Object.values(values).some((v) => v !== null && Number.isNaN(v));

  const submit = (e: Event) => {
    e.preventDefault();
    void onSave(() =>
      api.setBudgets({ expected_version: data.version, reason: reason.trim(), ...values }),
    );
  };

  return (
    <section class="card">
      <h2>{t("ops.budgets")}</h2>
      <PanelHelp k="ops.budgets" />
      <form onSubmit={submit}>
        <table class="table">
          <thead>
            <tr>
              <th>{t("ops.budget.col.budget")}</th>
              <th>{t("ops.budget.col.baseline")}</th>
              <th>{t("ops.budget.col.effective")}</th>
              <th>{t("ops.budget.col.new")}</th>
            </tr>
          </thead>
          <tbody>
            {data.budgets.map((b) => (
              <tr key={b.key}>
                <td>{t(BUDGET_LABEL[b.key])}</td>
                <td>{usdOrOff(b.baseline_usd)}</td>
                <td>{usdOrOff(b.effective_usd)}</td>
                <td>
                  <input
                    id={budgetField(b.key)}
                    class="ops-input"
                    type="text"
                    inputMode="decimal"
                    aria-label={t(BUDGET_LABEL[b.key])}
                    value={drafts[b.key]}
                    placeholder={t("ops.budget.placeholder")}
                    onInput={(e) =>
                      setDrafts({ ...drafts, [b.key]: (e.target as HTMLInputElement).value })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invalid && <div class="hl-orange">{t("ops.budget.invalid")}</div>}
        <div class="kpi-sub">{t("ops.budget.note")}</div>
        <button type="submit" class="window-pill active" disabled={!canSave || invalid}>
          {t("ops.budget.save")}
        </button>
      </form>
    </section>
  );
}

function modeState(row: RuntimeModeRow): string {
  if (!row.released) return t("ops.mode.unreleased");
  return row.enabled ? t("common.on") : t("common.off");
}

function ModesCard({ data, reason, canSave, onSave }: CardProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const saveAndClose: Save = async (change) => {
    const ok = await onSave(change);
    if (ok) setEditing(null);
    return ok;
  };
  return (
    <section class="card">
      <h2>{t("ops.modes")}</h2>
      <PanelHelp k="ops.modes" />
      <table class="table">
        <thead>
          <tr>
            <th>{t("common.mode")}</th>
            <th>{t("ops.mode.col.state")}</th>
            <th>{t("ops.mode.col.effective")}</th>
            <th>{t("ops.mode.col.test")}</th>
            <th>{t("ops.mode.col.baseline")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.modes.map((row) => (
            <Fragment key={row.mode}>
              <tr>
                <td>{modeLabel(row.mode)}</td>
                <td class={row.released && !row.enabled ? "hl-orange" : ""}>{modeState(row)}</td>
                <td>{runSummary(row.effective)}</td>
                <td class={sameRun(row.test_sessions, row.effective) ? "" : "hl-orange"}>
                  {runSummary(row.test_sessions)}
                </td>
                <td>{runSummary(row.baseline)}</td>
                <td>
                  {row.released && (
                    <button
                      type="button"
                      class="window-pill"
                      onClick={() => setEditing(editing === row.mode ? null : row.mode)}
                    >
                      {editing === row.mode ? t("ops.mode.cancel") : t("ops.mode.edit")}
                    </button>
                  )}
                </td>
              </tr>
              {editing === row.mode && (
                <tr>
                  <td colSpan={6}>
                    <ModeForm
                      row={row}
                      version={data.version}
                      reason={reason}
                      canSave={canSave}
                      onSave={saveAndClose}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ModeForm({
  row,
  version,
  reason,
  canSave,
  onSave,
}: {
  row: RuntimeModeRow;
  version: number;
  reason: string;
  canSave: boolean;
  onSave: Save;
}) {
  const [draft, setDraft] = useState<ModeDraft>(() => switchDraft(row));
  const update = (patch: Partial<ModeDraft>) => setDraft({ ...draft, ...patch });
  const depths = depthOptions(row, draft.model);
  const modelDefault = t("ops.modelDefault", {
    depth: choiceFor(row, draft.model)?.default_depth ?? row.baseline.default_depth,
  });
  const terraformDepth =
    row.baseline.depth || t("ops.modelDefault", { depth: row.baseline.default_depth });

  const send = (next: ModeDraft) =>
    void onSave(() => api.switchMode(row.mode, modeSwitchBody(row, next, version, reason)));

  return (
    <form
      class="toolbar ops-form"
      onSubmit={(e) => {
        e.preventDefault();
        send(draft);
      }}
    >
      <label class="toggle">
        <input
          type="checkbox"
          checked={draft.enabled}
          onInput={(e) => update({ enabled: (e.target as HTMLInputElement).checked })}
        />
        <span>{t("ops.mode.enabled")}</span>
      </label>
      <label class="toggle">
        <span>{t("ops.mode.model")}</span>
        <select
          value={draft.model}
          onInput={(e) => {
            const model = (e.target as HTMLSelectElement).value;
            // A depth the new model does not take falls back to Terraform's.
            const keep = draft.depth === null || depthOptions(row, model).includes(draft.depth);
            update({ model, depth: keep ? draft.depth : null });
          }}
        >
          <option value="">{t("ops.mode.model.baseline", { model: row.baseline.model_label })}</option>
          {byProvider(row.choices).map(([provider, choices]) => (
            <optgroup key={provider} label={PROVIDER_LABEL[provider]}>
              {choices.map((c) => (
                <option key={c.model_id} value={c.model_id}>
                  {c.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <label class="toggle">
        <span>{t("ops.mode.depth")}</span>
        <select
          value={draft.depth ?? TERRAFORM}
          onInput={(e) => {
            const value = (e.target as HTMLSelectElement).value;
            update({ depth: value === TERRAFORM ? null : value });
          }}
        >
          <option value={TERRAFORM}>{t("ops.mode.depth.baseline", { depth: terraformDepth })}</option>
          {depths.map((d) => (
            <option key={d || "model-default"} value={d}>
              {d || modelDefault}
            </option>
          ))}
        </select>
      </label>
      <label class="toggle">
        <span>{t("ops.mode.scope")}</span>
        <select
          value={draft.scope}
          onInput={(e) =>
            update({ scope: (e.target as HTMLSelectElement).value === "test" ? "test" : "all" })
          }
        >
          <option value="all">{t("ops.mode.scope.all")}</option>
          <option value="test">{t("ops.mode.scope.test")}</option>
        </select>
      </label>
      <button type="submit" class="window-pill active" disabled={!canSave}>
        {t("ops.mode.save")}
      </button>
      <button
        type="button"
        class="window-pill"
        disabled={!canSave || !row.overridden}
        onClick={() => send({ enabled: true, model: "", depth: null, scope: "all" })}
      >
        {t("ops.mode.reset")}
      </button>
    </form>
  );
}

function LogCard({ log }: { log: RuntimeLogRow[] }) {
  return (
    <section class="card">
      <h2>{t("ops.log")}</h2>
      <PanelHelp k="ops.log" />
      {log.length === 0 ? (
        <div class="kpi-sub">{t("ops.log.empty")}</div>
      ) : (
        <table class="table">
          <thead>
            <tr>
              <th>{t("ops.log.col.version")}</th>
              <th>{t("ops.log.col.at")}</th>
              <th>{t("ops.log.col.actor")}</th>
              <th>{t("ops.log.col.reason")}</th>
              <th>{t("ops.log.col.change")}</th>
            </tr>
          </thead>
          <tbody>
            {log.map((row) => (
              <tr key={row.version}>
                <td>{formatInt(row.version)}</td>
                <td>{formatDateTime(row.at)}</td>
                <td>{row.actor}</td>
                <td>{row.reason}</td>
                <td>
                  <code class="ops-change">{row.summary.join("\n")}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function WipeCard({
  version,
  reason,
  onLogged,
  onDone,
}: {
  version: number | undefined;
  reason: string;
  /** The reset went through, with its reason in the log. */
  onLogged: () => void;
  onDone: () => void;
}) {
  const preview = usePoll((s) => api.wipePreview(s), [], { intervalMs: 5 * 60_000 });
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<WipeResponse | null>(null);
  const counts = preview.data;
  const ready =
    counts !== undefined && version !== undefined && !busy && wipeReady(counts, confirm, password, reason);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!ready || version === undefined) return;
    setBusy(true);
    setError("");
    try {
      setDone(
        await api.wipe({
          expected_version: version,
          reason: reason.trim(),
          confirm_text: confirm,
          password,
        }),
      );
      setConfirm("");
      onLogged();
    } catch (err) {
      setError(runtimeErrorMessage(err));
    } finally {
      // The password never outlives an attempt.
      setPassword("");
      setBusy(false);
      preview.refetch();
      onDone();
    }
  };

  return (
    <section class="card ops-danger">
      <h2>{t("ops.wipe")}</h2>
      <PanelHelp k="ops.wipe" />
      {counts && (
        <div class="kpi-sub">
          {t("ops.wipe.counts", {
            sessions: formatInt(counts.sessions),
            turns: formatInt(counts.turns),
            events: formatInt(counts.analytics_events),
            aggregates: formatInt(counts.analytics_aggregates),
          })}
        </div>
      )}
      {counts && !counts.available && <div class="hl-orange">{t("ops.wipe.unavailable")}</div>}
      {counts?.available && (
        <form class="toolbar ops-form" onSubmit={submit}>
          <label class="toggle">
            <span>{t("ops.wipe.confirm", { text: counts.confirm_text })}</span>
            <input
              type="text"
              autocomplete="off"
              spellcheck={false}
              value={confirm}
              onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
            />
          </label>
          <label class="toggle">
            <span>{t("ops.wipe.password")}</span>
            <input
              type="password"
              autocomplete="off"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            />
          </label>
          <button type="submit" class="window-pill ops-danger-button" disabled={!ready}>
            {busy ? t("ops.wipe.busy") : t("ops.wipe.submit")}
          </button>
        </form>
      )}
      {error && (
        <div class="error-banner" role="alert">
          {error}
        </div>
      )}
      {done && (
        <div class="kpi-sub" role="status">
          {t("ops.wipe.done", {
            sessions: formatInt(done.deleted.sessions),
            turns: formatInt(done.deleted.turns),
            events: formatInt(done.deleted.analytics_events),
            aggregates: formatInt(done.deleted.analytics_aggregates),
            at: formatDateTime(done.wiped_at),
          })}
        </div>
      )}
    </section>
  );
}
